import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import {
  toggleFoupPickupLabels,
  toggleOhtLabels,
  toggleOhtRouteLabels,
  toggleSimulationTrackNodes,
  toggleSupportPillars,
} from '../../features/ui/uiSlice';
import { toggleShowPorts } from '../../features/equipment/equipmentSlice';

type LabelToggleRow = {
  id: string;
  label: string;
  hint: string;
  checked: boolean;
  onToggle: () => void;
};

const LabelsTogglePopup: React.FC = () => {
  const dispatch = useAppDispatch();
  const [open, setOpen] = useState(false);

  const showSimulationTrackNodes = useAppSelector(
    (state) => state.ui.showSimulationTrackNodes
  );
  const showSupportPillars = useAppSelector((state) => state.ui.showSupportPillars);
  const showFoupPickupLabels = useAppSelector(
    (state) => state.ui.showFoupPickupLabels ?? true
  );
  const showOhtLabels = useAppSelector((state) => state.ui.showOhtLabels ?? false);
  const showOhtRouteLabels = useAppSelector(
    (state) => state.ui.showOhtRouteLabels ?? true
  );
  const showLoadPorts = useAppSelector((state) => state.equipment.showPorts);

  const rows: LabelToggleRow[] = [
    {
      id: 'track-nodes',
      label: 'Track network nodes',
      hint: 'Junction and network node markers on the 2D canvas. Entry and exit stay visible.',
      checked: showSimulationTrackNodes,
      onToggle: () => dispatch(toggleSimulationTrackNodes()),
    },
    {
      id: 'pillars',
      label: '3D support pillars',
      hint: 'Pillars and hanging suspension rods in the 3D scene',
      checked: showSupportPillars,
      onToggle: () => dispatch(toggleSupportPillars()),
    },
    {
      id: 'foup-pickup',
      label: 'Show FOUP pickup labels',
      hint: 'Badge on a tool once its FOUP is processed and waiting',
      checked: showFoupPickupLabels,
      onToggle: () => dispatch(toggleFoupPickupLabels()),
    },
    {
      id: 'oht-labels',
      label: 'Show OHT labels',
      hint: 'Keep vehicle names visible instead of only on hover',
      checked: showOhtLabels,
      onToggle: () => dispatch(toggleOhtLabels()),
    },
    {
      id: 'oht-route-labels',
      label: 'Show OHT route labels',
      hint: 'Travel / Pick From / Drop To chips, e.g. Travel → EQ1-LP1',
      checked: showOhtRouteLabels,
      onToggle: () => dispatch(toggleOhtRouteLabels()),
    },
    {
      id: 'loadports',
      label: 'Show loadports (2D)',
      hint: 'Load-port markers on equipment and stockers',
      checked: showLoadPorts,
      onToggle: () => dispatch(toggleShowPorts()),
    },
  ];

  useEffect(() => {
    if (!open) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        title="Toggle labels"
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200"
      >
        Toggle labels
      </button>

      {open &&
        createPortal(
          <div
            className="fixed inset-0 z-1000 flex items-center justify-center bg-slate-900/40 p-4"
            onClick={() => setOpen(false)}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Toggle labels"
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-sm rounded-xl border border-slate-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-900"
            >
              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3 dark:border-gray-700">
                <h2 className="text-sm font-semibold text-slate-900 dark:text-gray-100">
                  Toggle labels
                </h2>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="rounded p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-gray-800"
                >
                  <svg
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    className="size-4"
                    aria-hidden
                  >
                    <path d="M5 5l10 10M15 5L5 15" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              <div className="flex flex-col divide-y divide-slate-100 px-2 py-1 dark:divide-gray-800">
                {rows.map((row) => (
                  <label
                    key={row.id}
                    className="flex cursor-pointer items-start justify-between gap-3 rounded px-2 py-2.5 hover:bg-slate-50 dark:hover:bg-gray-800"
                  >
                    <span className="flex flex-col">
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-200">
                        {row.label}
                      </span>
                      <span className="text-[11px] text-slate-400">{row.hint}</span>
                    </span>
                    <input
                      type="checkbox"
                      checked={row.checked}
                      onChange={row.onToggle}
                      className="mt-0.5 size-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                ))}
              </div>

              <div className="flex justify-end border-t border-slate-200 px-4 py-3 dark:border-gray-700">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded border border-slate-300 bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-200"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default LabelsTogglePopup;
